import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Clock, Mountain } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { useBooking } from "@/context/BookingContext";

// Note: file names vs content were mapped after visual check.
const IMG = {
  summit: "/images/kedarkantha-valley.webp", // sunset summit (snowboarder/skier)
  panorama: "/images/kedarkantha-summit.webp", // wide snowy valley
  chai: "/images/kedarkantha-chai.webp", // chai over the valley
  maggi: "/images/kedarkantha-forest.webp", // maggi bowl
  forest: "/images/kedarkantha-maggi.webp", // snowy trees / valley
};

const ITINERARY = [
  {
    day: "Day 01",
    title: "Dehradun → Sankri → Aruha",
    body: "A long, scenic drive up curving mountain roads. Arrive at Aruha by evening — our cafe & stay, a short 3 km hike from Sankri market — to a warm welcome with tea and refreshments. Settle into the traditional wooden house, soak in the views, and enjoy a local-cuisine dinner. Rest well.",
    image: IMG.chai,
  },
  {
    day: "Day 02",
    title: "Juda ka Talab hike · Acclimatisation at Aruha",
    body: "After breakfast, hike up to the frozen lake of Juda ka Talab through pine and oak forest, then return to Aruha to acclimatise. An easy, restful day at the property — warm meals, mountain air, and cozy beds to prepare your body for the climb ahead.",
    image: IMG.forest,
  },
  {
    day: "Day 03",
    title: "Aruha → Kedarkantha Base",
    body: "Trek from Aruha to Kedarkantha Base through snow-laden clearings, with sweeping views of Swargarohini, Bandarpoonch and Ranglana. Arrive to warm tea and refreshments, an early dinner in the mountains, and rest before summit day.",
    image: IMG.panorama,
  },
  {
    day: "Day 04",
    title: "Summit day: Base → Kedarkantha Peak → back to Aruha",
    meta: "Summit ~3,810 m / 12,500 ft",
    body: "A pre-dawn start for a sunrise summit over a white Himalaya. Celebrate at the top, then descend all the way back to Aruha by evening — warm tea, a hot meal, and cozy beds in the traditional house.",
    image: IMG.summit,
  },
  {
    day: "Day 05",
    title: "Aruha → Dehradun",
    body: "Early-morning tea and packed sandwiches for the road, then drive back to Dehradun — carrying the mountains home with you.",
    image: IMG.maggi,
  },
];

export default function Kedarkantha() {
  const { openTour } = useBooking();
  const book = () => openTour("Kedarkantha Winter Trek");
  const [isAdTraffic, setIsAdTraffic] = useState(false);
  const [formData, setFormData] = useState({ name: "", whatsapp: "", email: "" });
  const [honeypot, setHoneypot] = useState(""); 
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("utm_source") === "instagram" || urlParams.get("ref") === "ig_ad") {
      setIsAdTraffic(true);
    }
  }, []);


  return (
    <div className="bg-[#f4f1ea] text-[#23281f]">
      <Navbar />

      {/* DYNAMIC INSTAGRAM AD LEAD GENERATION CONSOLE */}
      {isAdTraffic && (
        <div className="w-full bg-[#fcfbfa] border-b border-[#e6e2da] py-10 px-6 transition-all duration-500">
          <div className="max-w-md mx-auto text-left">
            <h3 className="text-xl font-serif font-medium tracking-tight text-[#23281f] mb-1.5">Priority Winter Booking Activation</h3>
            <p className="text-[11px] text-neutral-500 mb-6 font-light leading-relaxed">
              Unlock priority reservation windows for our upcoming November-to-April mountain batches and claim your verified zero-waste winter gear survival manual.
            </p>

            {status === "success" ? (
              <div className="bg-white border border-[#e6e2da] p-4 text-center">
                <p className="text-xs font-medium text-emerald-800">✓ System Connection Secured</p>
                <p className="text-[11px] text-neutral-500 mt-1">Your premium route itinerary and packing checklist is downloading automatically.</p>
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (honeypot) return;
                setStatus("submitting");
                try {
                  const res = await fetch("https://aruha-backend.onrender.com", {

                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ ...formData, source: "instagram_ad_kedarkantha" }),
                  });
                  if (res.ok) {
                    setStatus("success");
                    setFormData({ name: "", whatsapp: "", email: "" });
                    window.open("/docs/kedarkantha-winter-guide.pdf", "_blank");
                  } else { setStatus("error"); }
                } catch { setStatus("error"); }
              }} className="space-y-4">
                
                {/* Anti-Spam Safety Layer */}
                <div className="hidden" aria-hidden="true">
                  <input type="text" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex="-1" />
                </div>

                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-neutral-400 font-medium mb-1">Full Name</label>
                  <input 
                    type="text" required value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-white border border-[#e6e2da] p-2.5 text-xs text-neutral-800 focus:outline-none focus:border-[#23281f] transition-colors rounded-none"
                    placeholder="Shivang Dhyani"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[9px] uppercase tracking-widest text-neutral-400 font-medium mb-1">WhatsApp Contact</label>
                    <input 
                      type="tel" required value={formData.whatsapp}
                      onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                      className="w-full bg-white border border-[#e6e2da] p-2.5 text-xs text-neutral-800 focus:outline-none focus:border-[#23281f] transition-colors rounded-none"
                      placeholder="+91 97596 XXXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] uppercase tracking-widest text-neutral-400 font-medium mb-1">Email Address</label>
                    <input 
                      type="email" required value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-white border border-[#e6e2da] p-2.5 text-xs text-neutral-800 focus:outline-none focus:border-[#23281f] transition-colors rounded-none"
                      placeholder="name@example.com"
                    />
                  </div>
                </div>

                {status === "error" && (
                  <p className="text-[10px] text-red-600 text-center font-medium">Transmission timed out. Please check details and retry.</p>
                )}

                <button 
                  type="submit" disabled={status === "submitting"}
                  className="w-full bg-[#23281f] text-white py-3 text-[10px] uppercase tracking-widest font-semibold hover:bg-neutral-800 disabled:bg-neutral-400 transition-colors rounded-none mt-2"
                >
                  {status === "submitting" ? "Syncing Workspace..." : "Unlock Winter Batches & Guide"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}


      <section data-testid="trek-hero" className="relative h-[92vh] w-full overflow-hidden bg-[#23281f]">
        <img src={IMG.summit} alt="Kedarkantha summit at sunrise" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "center 35%" }} />
        <div className="absolute inset-0 bg-[#12160f]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d110d] via-[#0d110d]/25 to-[#0d110d]/55" />
        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-20 lg:px-10">
          <a href="/#adventures" className="mb-6 inline-flex w-fit items-center gap-2 text-sm text-[#e4e2da] hover:text-white">
            <ArrowLeft className="h-4 w-4" /> All experiences
          </a>
          <div className="mb-4 inline-block w-fit rounded-none bg-black/45 px-3 py-1.5 backdrop-blur-sm">
            <span className="overline !text-[#f2e4c9]">Trekking &amp; Tours · Winter</span>
          </div>
          <h1 className="max-w-3xl font-serif text-5xl font-medium leading-[0.95] tracking-tight text-[#f6f3ec] text-shadow-soft lg:text-8xl">
            Kedarkantha <span className="italic text-[#e7d2ad]">Winter Trek</span>
          </h1>
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-[#e4e2da]">
            <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-[#e7d2ad]" /> 5 Days / 4 Nights</span>
            <span className="inline-flex items-center gap-2"><Mountain className="h-4 w-4 text-[#e7d2ad]" /> Summit ~3,810 m</span>
            <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-[#e7d2ad]" /> Sankri · Aruha · Kedarkantha</span>
          </div>
          <button
            data-testid="trek-book-btn"
            onClick={book}
            className="mt-8 inline-flex w-fit rounded-none bg-[#ba5a45] px-9 py-4 text-xs font-semibold uppercase tracking-widest text-[#f6f3ec] transition-colors hover:bg-[#9c4633]"
          >
            Enquire / Book this trek
          </button>
        </div>
      </section>

      <section className="px-6 py-24 lg:px-10">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="overline mb-6">The Journey</div>
            <p className="font-serif text-2xl leading-relaxed text-[#3a4038] lg:text-3xl">
              India's most-loved winter summit — snow-laced ridgelines, pine forests, and a sunrise from
              12,500 ft. Based entirely at Aruha, our cafe & stay near Sankri, with warm meals, yoga,
              and a proper acclimatisation day built in.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#eae4d8] px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto max-w-[1200px]">
          <Reveal>
            <div className="overline mb-6">Day-by-day</div>
            <h2 className="mb-16 font-serif text-4xl font-medium tracking-tight text-[#23281f] lg:text-6xl">
              The <span className="italic text-[#ba5a45]">itinerary.</span>
            </h2>
          </Reveal>
          <div className="space-y-16 lg:space-y-24">
            {ITINERARY.map((d, i) => (
              <motion.div
                key={d.day}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-14 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={d.image} alt={d.title} className="h-full w-full object-cover" style={{ objectPosition: "center 45%" }} />
                </div>
                <div>
                  <div className="font-serif text-6xl leading-none text-[#c9bfa9] lg:text-7xl">{d.day.split(" ")[1]}</div>
                  <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[#ba5a45]">{d.day}</div>
                  <h3 className="mt-3 font-serif text-3xl text-[#23281f] lg:text-4xl">{d.title}</h3>
                  {d.meta && <div className="mt-2 text-sm font-medium text-[#8a7a62]">{d.meta}</div>}
                  <p className="mt-4 max-w-xl leading-relaxed text-[#5c6157]">{d.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-24 lg:px-10">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="overline mb-6">From the summit</div>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6">
            {[IMG.panorama, IMG.chai, IMG.maggi, IMG.forest].map((src, i) => (
              <Reveal key={i} delay={(i % 4) * 0.06} className="group aspect-[3/4] overflow-hidden">
                <img src={src} alt="Kedarkantha trek" className="img-zoom h-full w-full object-cover" />
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14 flex flex-col items-center gap-6 border-t border-[#ddd6c8] pt-14 text-center">
              <h3 className="font-serif text-3xl text-[#23281f] lg:text-5xl">Summit the winter king</h3>
              <button
                data-testid="trek-book-btn-bottom"
                onClick={book}
                className="rounded-none bg-[#ba5a45] px-10 py-4 text-xs font-semibold uppercase tracking-widest text-[#f6f3ec] transition-colors hover:bg-[#9c4633]"
              >
                Enquire / Book this trek
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
